/**
 * OfflineEventModal — wizard for planning an offline meet-up.
 * Extracted from CreatorModals.tsx (AMB-232 slice 1). Behaviour is byte-
 * for-byte identical; only the surrounding scaffolding (formatters,
 * style helpers, date utilities) was moved to creatorModalsShared.tsx
 * so each modal can live in its own file without dragging the shared
 * code with it.
 *
 * AMB-213 — offline events use the dedicated MasterEvent entity
 * (api.createEvent → POST /api/master/content/events). CORE-227 — the
 * master opts in/out of the feed announcement via `publishAnnouncement`;
 * the BE posts it (and uses `feedMessage` as the body) when enabled.
 */
import React, { useMemo, useState } from 'react';
import { isModerationBlock, moderationRefusalMessage, friendlyApiError } from '../../services/errorMessages';
import {
    View,
    Text,
    Modal,
    TouchableOpacity,
    TextInput,
    ScrollView,
    Image,
    ActivityIndicator,
    Platform,
    DeviceEventEmitter,
    useWindowDimensions,
} from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import * as ImagePicker from 'expo-image-picker';

import { api, unsplashPhotoUrl } from '../../services/api';
import { uploadCoverWithCeiling, CoverUploadTimeoutError } from './liveWizardCoverUpload';
import { COVER_LIST_TIMEOUT_MS, COVER_PHOTO_TIMEOUT_MS } from '../../screens/live-session/create/coverStepTimeouts';
import { DatePickerField, TimePickerField } from '../ui/DateTimePickers';
import type { MasterEvent, UnsplashImage } from '../../types/auth';
import { serializeDirections } from '../../constants/directions';
import { useDirections } from '../../hooks/useDirections';
import { useI18n } from '../../i18n';
import {
    type DurationUnit,
    durationToMinutes,
    deriveDurationValueUnit,
    formatEventDuration,
    DURATION_UNIT_LABEL,
    DURATION_UNIT_ORDER,
} from '../../utils/eventDuration';

import {
    ACCENT,
    fieldLabel,
    inputStyle,
    primaryBtn,
    orangeBtn,
    localISODate,
    localHHMM,
    todayISO,
    nextValidStart,
    isFutureStart,
    PUBLISH_DISCLAIMER,
    type LiveModalStep,
    formatPreviewDate,
} from './creatorModalsShared';
import { buildShareText, shareEntityOrModal, type ShareEntity } from '../../utils/roomShare';
import { EntityShareModal } from '../EntityShareModal';
import { nextOfflineEventStep, previousOfflineEventStep } from '../../screens/events/offlineEventFlow';
import { fontWeights } from '../../styles/typographyTokens';

import { ON_ACCENT_TEXT, accentTextOn } from '../../styles/accentText';
import { surfaceFill } from '../../styles/platformBackgrounds';
const SHORT_DESCRIPTION_MAX = 200;
const DETAILS_MAX = 2000;
const FEED_MESSAGE_MAX = 2000;

interface OfflineEventModalProps {
    visible: boolean;
    onClose: () => void;
    /** Fires with the created MasterEvent. AMB-213 — was MasterLive, switched
     *  to MasterEvent when the offline path moved to the dedicated entity. */
    onCreated?: (event: MasterEvent) => void;
    isDark: boolean;
    colors: any;
    /** Pre-fill mode for edit. AMB-213 — type was MasterLive, now we accept
     *  the new MasterEvent shape. Edit path is currently unused in the app
     *  (callers create-only) but the prop stays in place for future re-use. */
    existingEvent?: MasterEvent | null;
    masterName?: string;
    /** Optional navigation handle — enables "Open event chat" shortcut on success step. */
    navigation?: any;
    /** Web creation is a routed, full-screen flow. Existing edit callers and
     * native keep the modal presentation. */
    screenMode?: boolean;
}

export const OfflineEventModal: React.FC<OfflineEventModalProps> = ({
    visible, onClose, onCreated, isDark, colors: tcProp, existingEvent, masterName, navigation, screenMode = false,
}) => {
    // The theme palette exposes `textPrimary`; this (web-origin) modal reads
    // `tc.text` throughout. Normalise so every `tc.text` reference resolves on
    // native too — palettes that already carry `.text` pass through unchanged,
    // so web output is identical.
    const tc = tcProp?.text ? tcProp : { ...tcProp, text: tcProp?.textPrimary };
    const isEdit = !!existingEvent;
    // CORE-111 — past events keep their fields visible but locked: the
    // spec is «у прошедших событий нельзя изменить время и место». We treat
    // an event as past once its start is in the past, so an in-progress
    // meet-up can't have its time/place moved either.
    const existingStartMs = existingEvent?.startDateUtc
        ? new Date(existingEvent.startDateUtc).getTime()
        : NaN;
    const lockTimePlace = isEdit && Number.isFinite(existingStartMs) && existingStartMs <= Date.now();
    const { t } = useI18n('common');
    const insets = useSafeAreaInsets();
    const { width: viewportWidth, height: viewportHeight } = useWindowDimensions();
    const compactWeb = screenMode && viewportWidth < 900;
    // The routed wizard must fit into the *visible* browser height. A 900px
    // laptop viewport was previously treated as the roomy desktop layout, so
    // the footer slipped below the fold. Switch to the dense composition
    // earlier and gently scale only the web flow on genuinely short screens.
    // ScrollView remains as an accessibility fallback for browser zoom / large
    // text instead of becoming the normal way to reach the primary action.
    const shortWeb = screenMode && viewportHeight < 1024;
    const webHeightScale = screenMode && Platform.OS === 'web'
        ? viewportHeight < 720 ? 0.78
            : viewportHeight < 800 ? 0.84
                : viewportHeight < 900 ? 0.90
                    : viewportHeight < 1024 ? 0.94
                        : 1
        : 1;
    const horizontalPreview = shortWeb && !compactWeb;
    const nestedSurface = tc.surfaceSecondary || tc.surfaceSoft || tc.surfaceElevated;
    // CORE-1733 (возврат QA 23.10) — /events/create как соседний мастер Live:
    // карточки формы и нижняя панель — поверхность v4 (были плоские #FFFFFF),
    // оранжевый в роли текста и глифа — акцентный текст (#E98245 на сливочном
    // 2.5:1), подписи на оранжевой заливке — ON_ACCENT_TEXT (белый 2.5–2.8:1).
    const panelFill = surfaceFill(isDark ? 'dark' : 'light');
    const ACCENT_INK = accentTextOn(ACCENT);
    // Звёздочка обязательного поля и текст ошибки: #EF4444 на светлой
    // поверхности давал 3.7:1. Натив — прежний.
    const REQUIRED_INK = Platform.OS !== 'web' ? '#EF4444' : (isDark ? '#FC8181' : '#C53030');
    // Native gets a full-screen «Загрузить практику»-style form; web keeps the
    // existing centred modal untouched (every platform fork falls back to the
    // original web JSX via the `: (…)` branch).
    const NATIVE = Platform.OS !== 'web';
    const [step, setStep] = useState<LiveModalStep>('edit');
    const [title, setTitle] = useState('');
    const [subtitle, setSubtitle] = useState('');
    const [description, setDescription] = useState('');
    const [directions, setDirections] = useState<string[]>([]);
    const directionOptions = useDirections();
    // Smart default — next 15-min mark from now (local tz). No buffer for
    // offline events: master can announce a meet-up starting any moment.
    const offlineInitialStart = useMemo(() => nextValidStart(0), []);
    const [date, setDate] = useState(offlineInitialStart.date);
    const [time, setTime] = useState(offlineInitialStart.time);
    const [duration, setDuration] = useState('60');
    // CORE-225 — unit for the numeric duration. Multi-day offline events
    // (retreats, festivals) are expressed as e.g. 14 «Дни»; the value is still
    // persisted as endDateUtc = start + value×unit, so no new BE field.
    const [durationUnit, setDurationUnit] = useState<DurationUnit>('minutes');
    const [address, setAddress] = useState('');
    const [coverFile, setCoverFile] = useState<any>(null);
    const [coverPreview, setCoverPreview] = useState<string | null>(null);
    const [publishToFeed, setPublishToFeed] = useState(true);
    // CORE-227 — master's personal invitation shown with the feed announcement.
    const [feedMessage, setFeedMessage] = useState('');
    // CORE-227 — opt-out of publishing the event announcement to the feed.
    // Default on; unchecking creates the event without a feed post.
    const [publishAnnouncement, setPublishAnnouncement] = useState(true);
    const [unsplashCovers, setUnsplashCovers] = useState<UnsplashImage[]>([]);
    const [coversLoading, setCoversLoading] = useState(false);
    // CORE-1992 — the gallery and a pick report their failure instead of
    // swallowing it: Unsplash's image host does not answer from Russia, and the
    // old empty catch left the master with a «chosen» cover that never loaded.
    const [coversFailed, setCoversFailed] = useState(false);
    const [pickingCoverId, setPickingCoverId] = useState<string | null>(null);
    const [coverError, setCoverError] = useState<string | null>(null);
    const [coverRetry, setCoverRetry] = useState<{ run: () => void } | null>(null);
    // AMB-213 — offline events are now MasterEvent entities. Was `createdLive: MasterLive`.
    const [createdEvent, setCreatedEvent] = useState<MasterEvent | null>(null);
    // CORE-786 — a phone gets the OS sheet, a desktop the «Поделиться» popup.
    const [shareOpen, setShareOpen] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [savedAsDraft, setSavedAsDraft] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const toggleDirection = (d: string) => {
        setDirections((prev) => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
    };

    // Native date/time inputs may update their visible value before React's
    // synthetic `change` event is delivered. Persist every `input` event so a
    // quick «Продолжить» press cannot move to step 2 with stale state.
    const handleWebDateInput = (event: React.FormEvent<HTMLInputElement>) => {
        setDate(event.currentTarget.value);
    };
    const handleWebTimeInput = (event: React.FormEvent<HTMLInputElement>) => {
        setTime(event.currentTarget.value);
    };

    const reset = () => {
        setStep('edit');
        setTitle(''); setSubtitle(''); setDescription(''); setAddress(''); setDirections([]);
        const start = nextValidStart(0);
        setDate(start.date); setTime(start.time); setDuration('60'); setDurationUnit('minutes');
        setCoverFile(null); setCoverPreview(null);
        setPickingCoverId(null); setCoverError(null); setCoverRetry(null);
        setPublishToFeed(true); setFeedMessage(''); setPublishAnnouncement(true);
        setCreatedEvent(null); setShareOpen(false);
        setError(null); setPublishing(false); setSavedAsDraft(false);
    };

    // CORE-1992 — the list has a ceiling (Live's CORE-1597 value); a failed or
    // stalled list says so and offers «Повторить» instead of spinning forever.
    const loadCovers = React.useCallback(() => {
        setCoversLoading(true);
        setCoversFailed(false);
        uploadCoverWithCeiling(api.getUnsplashLiveCovers(), COVER_LIST_TIMEOUT_MS)
            .then(list => setUnsplashCovers(Array.isArray(list) ? list : []))
            .catch(() => { setUnsplashCovers([]); setCoversFailed(true); })
            .finally(() => setCoversLoading(false));
    }, []);

    React.useEffect(() => {
        if (!visible) return;
        loadCovers();

        if (existingEvent) {
            // AMB-213 — MasterEvent has dedicated fields, so the legacy
            // address-in-description hack from MasterLive is gone:
            //   address → existingEvent.address
            //   description → existingEvent.description (no «📍 » prefix)
            //   directions → existingEvent.tags (array)
            //   duration → end - start in minutes
            const startDate = new Date(existingEvent.startDateUtc);
            setTitle(existingEvent.title || '');
            setAddress(existingEvent.address || existingEvent.location || '');
            setSubtitle(existingEvent.subtitle || '');
            setDescription(existingEvent.description || '');
            setDirections(Array.isArray(existingEvent.tags) ? existingEvent.tags : []);
            // Use LOCAL date/time helpers — `.toISOString().slice(0,10)`
            // returns UTC date which silently rolls ±1 day for events near
            // local midnight. The pencil-edit on an evening announcement
            // would hydrate with «day later» before this fix.
            setDate(localISODate(startDate));
            setTime(localHHMM(startDate));
            const durationMin = existingEvent.endDateUtc
                ? Math.max(0, Math.round((new Date(existingEvent.endDateUtc).getTime() - startDate.getTime()) / 60000))
                : 60;
            // CORE-225 — re-express the minute delta in its natural unit so a
            // 14-day event opens as "14" + «Дни», not "20160" + «Минуты».
            const { value: durValue, unit: durUnit } = deriveDurationValueUnit(durationMin || 60);
            setDuration(String(durValue));
            setDurationUnit(durUnit);
            setCoverPreview(existingEvent.coverImageUrl || null);
            setCoverFile(null);
            // CORE-89 — MasterEvent.isPrivate is now persisted. Default
            // back to «Публичное» when the BE field is absent (older rows
            // pre-CORE-89, or a payload from a stale FE cache).
            setPublishToFeed(!existingEvent.isPrivate);
            setStep('edit');
        } else {
            reset();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, existingEvent?.id]);

    const handleClose = () => { reset(); onClose(); };

    const handlePickCover = async () => {
        const res = await ImagePicker.launchImageLibraryAsync({
            mediaTypes: ['images'], allowsEditing: true, aspect: [16, 9], quality: 0.85,
        });
        if (!res.canceled && res.assets[0]) {
            const asset = res.assets[0];
            setCoverPreview(asset.uri);
            if (Platform.OS === 'web') {
                const blob = await fetch(asset.uri).then(r => r.blob());
                setCoverFile(new File([blob], 'cover.jpg', { type: blob.type || 'image/jpeg' }));
            } else {
                setCoverFile({ uri: asset.uri, name: 'cover.jpg', type: 'image/jpeg' } as any);
            }
        }
    };

    // CORE-1992 — the photo comes through our API (`unsplashPhotoUrl`, CORE-1545),
    // never from Unsplash's image host: it times out from Russia. The pick
    // has a ceiling; the cover is shown as chosen only once the photo arrived,
    // and a failure keeps the previous cover and offers «Повторить».
    const handlePickUnsplash = async (img: UnsplashImage) => {
        if (pickingCoverId) return;
        const regularUrl = unsplashPhotoUrl(img.id, 'regular');
        setPickingCoverId(img.id);
        setCoverError(null);
        setCoverRetry(null);
        try {
            const blob = await uploadCoverWithCeiling((async () => {
                // CORE-1997 — no-store: the <img> preview's cached copy has no CORS headers.
                const res = await fetch(regularUrl, { cache: 'no-store' });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.blob();
            })(), COVER_PHOTO_TIMEOUT_MS);
            if (Platform.OS === 'web') {
                setCoverFile(new File([blob], `unsplash-${img.id}.jpg`, { type: blob.type || 'image/jpeg' }));
            } else {
                setCoverFile({ uri: regularUrl, name: `unsplash-${img.id}.jpg`, type: 'image/jpeg' } as any);
            }
            setCoverPreview(regularUrl);
            api.trackUnsplashDownload(img.downloadLocationUrl).catch(() => { /* non-fatal */ });
        } catch (e) {
            setCoverError(e instanceof CoverUploadTimeoutError
                ? e.message
                : 'Не получилось взять эту обложку. Попробуйте ещё раз, выберите другую или загрузите свою.');
            setCoverRetry({ run: () => { void handlePickUnsplash(img); } });
        } finally {
            setPickingCoverId(null);
        }
    };

    const coverStatus = (
        <>
            {coversFailed && !coversLoading && (
                <View testID="offline-event-covers-failed" style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
                    <Text style={{ color: REQUIRED_INK, fontSize: 13 }}>Подборка обложек не загрузилась.</Text>
                    <TouchableOpacity testID="offline-event-covers-retry" accessibilityRole="button" onPress={loadCovers} activeOpacity={0.85}>
                        <Text style={{ color: ACCENT_INK, fontSize: 13, fontWeight: fontWeights.bold }}>Повторить</Text>
                    </TouchableOpacity>
                </View>
            )}
            {coverError && (
                <View testID="offline-event-cover-error" style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>
                    <Text style={{ color: REQUIRED_INK, fontSize: 13, flexShrink: 1 }}>{coverError}</Text>
                    {coverRetry && (
                        <TouchableOpacity testID="offline-event-cover-retry" accessibilityRole="button" onPress={coverRetry.run} activeOpacity={0.85}>
                            <Text style={{ color: ACCENT_INK, fontSize: 13, fontWeight: fontWeights.bold }}>Повторить</Text>
                        </TouchableOpacity>
                    )}
                </View>
            )}
        </>
    );

    const canPreview = title.trim().length >= 3 && address.trim().length >= 3 && directions.length > 0 && !publishing;

    // AMB-197 — validation is now per-field with explicit messages. The
    // button is rendered as visually-disabled when `canPreview` is false
    // but still receives onPress so a tester pressing it gets actionable
    // feedback instead of silent no-op (the original bug: `disabled` on
    // TouchableOpacity blocked the press, the inline «выберите направление»
    // hint was missed, and the button looked frozen).
    const handleGoToPreview = () => {
        if (publishing) return;
        if (title.trim().length < 3) {
            setError('Введите название события — минимум 3 символа.');
            return;
        }
        if (address.trim().length < 3) {
            setError('Укажите адрес или место встречи.');
            return;
        }
        if (directions.length === 0) {
            setError('Выберите хотя бы одно направление — иначе событие не попадёт в фильтры календаря.');
            return;
        }
        // Past events keep their original (locked) date/time, so the
        // future-start guard would always fail — skip it for them.
        if (!lockTimePlace && !isFutureStart(date, time)) {
            setError('Дата и время начала должны быть в будущем.');
            return;
        }
        setError(null);
        // Leave feedMessage empty when the master hasn't typed anything — the
        // BE then falls back to the event description/title for the announcement
        // post body instead of an empty quote line.
        setStep(nextOfflineEventStep(step, screenMode));
    };

    const handleSaveDraft = () => {
        if (publishing) return;
        if (title.trim().length < 3) {
            setError('Введите название события — минимум 3 символа.');
            return;
        }
        if (address.trim().length < 3) {
            setError('Укажите адрес или место встречи.');
            return;
        }
        if (directions.length === 0) {
            setError('Выберите хотя бы одно направление — иначе событие не попадёт в фильтры календаря.');
            return;
        }
        void handlePublish(true);
    };

    async function handlePublish(asDraft = false) {
        // The wizard can stay open long enough for a previously-valid default
        // start time to pass. Re-check immediately before the request instead
        // of letting the BE answer with an opaque 400 on the final step.
        if (!lockTimePlace && !isFutureStart(date, time)) {
            setStep('edit');
            setError('Время начала уже прошло. Выберите новое время и продолжите публикацию.');
            return;
        }
        setPublishing(true); setError(null); setSavedAsDraft(asDraft);
        try {
            // AMB-213 — switched from api.createLive(type='Офлайн') to the
            // dedicated MasterEvent path. Field mapping:
            //   title              → title
            //   address            → address (its own column, no '📍 ' prefix)
            //   description        → description (raw, no address prefix)
            //   directions         → tags (CSV — BE ParseTags splits on ',')
            //   date + time        → startDateUtc (ISO)
            //   duration (mins)    → endDateUtc = start + duration*60s
            //   coverFile          → coverImage (multipart)
            //   eventType          → 'meetup' (default for ad-hoc offline)
            //   isOnline           → false (this surface is offline-only)
            //   feedMessage        → FeedMessage (announcement post body)
            //   publishAnnouncement→ PublishAnnouncement (skip post when false)
            //
            // CORE-227 — the BE posts the feed announcement (deeplink
            // /master/{profileId}/events/{eventId}) only when
            // publishAnnouncement is true. No `setLiveVisibility` /
            // `createLivePreviewToken` — those are MasterLive-only concepts.
            const startMs = new Date(`${date}T${time}:00`).getTime();
            // CORE-225 — value × unit → minutes → endDateUtc. Days/hours let a
            // master schedule multi-day meet-ups; the wire format is unchanged.
            const durationMin = durationToMinutes(parseInt(duration, 10) || 0, durationUnit) || 60;
            const durationSec = durationMin * 60;
            const startISO = new Date(startMs).toISOString();
            const endISO = new Date(startMs + durationSec * 1000).toISOString();
            const trimmedAddress = address.trim();
            const trimmedSubtitle = subtitle.trim();
            const trimmedDescription = description.trim();

            // CORE-111 — edit path. Same field mapping as create, routed to
            // PUT /api/master/content/events/{id}. The cover is only sent
            // when the master picked a new one (coverFile set); otherwise the
            // existing cover stays. BE keeps everything owner-scoped (403 for
            // non-owners), so no extra guard is needed here.
            if (isEdit && existingEvent) {
                const updated = await api.updateEvent(existingEvent.id, {
                    title: title.trim(),
                    eventType: existingEvent.eventType || 'meetup',
                    startDateUtc: startISO,
                    endDateUtc: endISO,
                    address: trimmedAddress || undefined,
                    location: trimmedAddress || undefined,
                    isOnline: false,
                    // Empty strings intentionally clear text saved earlier.
                    subtitle: trimmedSubtitle,
                    description: trimmedDescription,
                    tags: serializeDirections(directions) || undefined,
                    coverImage: coverFile || undefined,
                    isPrivate: asDraft || !publishToFeed,
                });
                setCreatedEvent(updated);
                // Tell the dashboard surfaces (#events / #studio) to refetch.
                DeviceEventEmitter.emit('master_events_changed');
                onCreated?.(updated);
                setStep('success');
                return;
            }

            const result = await api.createEvent({
                title: title.trim(),
                eventType: 'meetup',
                startDateUtc: startISO,
                endDateUtc: endISO,
                address: trimmedAddress || undefined,
                location: trimmedAddress || undefined,
                isOnline: false,
                subtitle: trimmedSubtitle || undefined,
                description: trimmedDescription || undefined,
                // BE accepts CSV string; ParseTags splits on ',' with trim.
                tags: serializeDirections(directions) || undefined,
                coverImage: coverFile || undefined,
                // CORE-89 — the «Публичное/Приватное» segmented selector
                // above maps publishToFeed=false → isPrivate=true so the
                // BE keeps the event off public calendars/dots/feed.
                isPrivate: asDraft || !publishToFeed,
                // CORE-227 — personal feed invitation + announcement opt-out.
                feedMessage: feedMessage.trim() || undefined,
                publishAnnouncement: asDraft ? false : publishAnnouncement,
            });
            setCreatedEvent(result);
            // Notify calendar / starting-soon listeners. `master_event_created`
            // is the offline-event-specific signal; the dashboard's «Ваши
            // события» list listens for it (kept «live_published» as a
            // belt-and-suspenders emit for any legacy listener that hadn't
            // moved yet — drop after the migration settles).
            DeviceEventEmitter.emit('master_event_created');
            DeviceEventEmitter.emit('master_events_changed');
            DeviceEventEmitter.emit('live_published');
            onCreated?.(result);
            setStep('success');
        } catch (e: any) {
            // Content-moderation block (BE 422) → branded RU copy, not the raw
            // English reason.
            // Friendly copy instead of the raw «Не удалось обработать ответ
            // сервера» — friendlyApiError maps timeouts / network / status codes
            // to warm RU and falls back to «Что-то пошло не так. Попробуйте ещё раз.»
            setError(isModerationBlock(e) ? moderationRefusalMessage(e) : friendlyApiError(e));
        } finally {
            setPublishing(false);
        }
    }

    // CORE-978 — the post-create share used to point at the master's own
    // profile URL, which is exactly the reported «ссылка ведёт на страницу
    // мастера, а не событие». The link now goes through the shared
    // helper, whose event URL is the CORE-630 `/lives?eventId=` deep link that
    // opens the event card, and the copy is localized (events:share.*) instead
    // of a hand-written Russian sentence.
    const share = createdEvent?.id
        ? buildShareText({ type: 'event', id: createdEvent.id, title })
        : null;
    const shareUrl = share?.url ?? null;

    const shareEntity: ShareEntity | null = createdEvent?.id
        ? { type: 'event', id: createdEvent.id, title }
        : null;
    const handleShare = () => {
        if (!shareEntity) return;
        // CORE-786 — a phone gets the OS sheet, a desktop the popup.
        void shareEntityOrModal(shareEntity, 'event_card', () => setShareOpen(true));
    };

    const handleGoToPublishedResult = () => {
        // When an announcement was requested, return to the master's feed: the
        // newly-created announcement is the freshest card there. The create
        // endpoint does not return its post id, so an exact focusPostId cannot
        // be supplied yet. Without an announcement, events is the useful target.
        if (!createdEvent?.id || !navigation) return;
        const targetTab = !savedAsDraft && publishAnnouncement ? 'feed' : 'events';
        // In routed screen mode `handleClose()` calls navigation.goBack().
        // Calling it after navigate() immediately undid this navigation and
        // returned the user to whichever screen happened to be underneath.
        reset();
        navigation.navigate('Dashboard', { initialTab: targetTab } as any);
    };

    // ── Native «edit» form — styled like the UploadPractice screen ──────────
    const naField = {
        borderWidth: 1, borderColor: ACCENT, borderRadius: 14,
        paddingHorizontal: 14, paddingVertical: 14,
        backgroundColor: tc.surfaceElevated, color: tc.text, fontSize: 15,
    } as any;
    const naLabel = { fontSize: 15, fontWeight: fontWeights.bold, color: tc.text, marginTop: 16, marginBottom: 8 } as any;
    const naHint = { fontSize: 12, color: tc.textMuted, lineHeight: 16, marginBottom: 8 } as any;

    const renderNativeEdit = () => (
        <>
            <Text style={naLabel}>Название события <Text style={{ color: ACCENT_INK }}>*</Text></Text>
            <TextInput
                testID="offline-event-title-input"
                accessibilityLabel="Название события"
                value={title}
                onChangeText={setTitle}
                placeholder="напр. Утренняя медитация на пляже"
                placeholderTextColor={tc.textMuted}
                style={naField}
            />

            <Text style={naLabel}>Адрес <Text style={{ color: ACCENT_INK }}>*</Text></Text>
            <TextInput
                testID="offline-event-address-input"
                accessibilityLabel="Адрес"
                value={address}
                onChangeText={setAddress}
                editable={!lockTimePlace}
                placeholder="Город, улица, дом, ориентир"
                placeholderTextColor={tc.textMuted}
                style={[naField, lockTimePlace && { opacity: 0.55 }]}
            />
            <Text style={[naHint, { marginTop: 6 }]}>
                {lockTimePlace ? 'Событие уже прошло — время и место изменить нельзя.' : 'Карта-метка будет добавлена в одном из следующих обновлений.'}
            </Text>

            {/* Date / Time / Duration */}
            <View style={{ flexDirection: 'row', gap: 10 }}>
                <View style={{ flex: 1.4 }}>
                    <Text style={naLabel}>Дата</Text>
                    <DatePickerField
                        testID="offline-event-date"
                        accessibilityLabel="Дата события"
                        value={date}
                        onChange={setDate}
                        minDate={todayISO()}
                        disabled={lockTimePlace}
                    />
                </View>
                <View style={{ flex: 1 }}>
                    <Text style={naLabel}>Время</Text>
                    <TimePickerField
                        testID="offline-event-time"
                        accessibilityLabel="Время события"
                        value={time}
                        onChange={setTime}
                        disabled={lockTimePlace}
                    />
                </View>
                <View style={{ width: 96 }}>
                    <Text style={naLabel}>Мин.</Text>
                    <TextInput
                        testID="offline-event-duration-input"
                        accessibilityLabel="Длительность"
                        value={duration}
                        onChangeText={(v) => setDuration(v.replace(/[^0-9]/g, ''))}
                        editable={!lockTimePlace}
                        keyboardType="numeric"
                        style={[naField, { textAlign: 'center' }, lockTimePlace && { opacity: 0.55 }]}
                    />
                </View>
            </View>

            <Text style={naLabel}>Краткое описание</Text>
            <TextInput
                testID="offline-event-subtitle-input"
                accessibilityLabel="Краткое описание"
                value={subtitle}
                onChangeText={setSubtitle}
                maxLength={SHORT_DESCRIPTION_MAX}
                placeholder="Коротко о событии — этот текст появится в карточке"
                placeholderTextColor={tc.textMuted}
                multiline
                style={[naField, { minHeight: 70, textAlignVertical: 'top' }]}
            />
            <Text style={[naHint, { textAlign: 'right', marginTop: 4 }]}>{subtitle.length} / {SHORT_DESCRIPTION_MAX}</Text>

            <Text style={naLabel}>Подробнее</Text>
            <TextInput
                testID="offline-event-description-input"
                accessibilityLabel="Подробнее"
                value={description}
                onChangeText={setDescription}
                maxLength={DETAILS_MAX}
                placeholder="Расскажите о программе, подготовке и важных деталях"
                placeholderTextColor={tc.textMuted}
                multiline
                style={[naField, { minHeight: 90, textAlignVertical: 'top' }]}
            />
            <Text style={[naHint, { textAlign: 'right', marginTop: 4 }]}>{description.length} / {DETAILS_MAX}</Text>

            <Text style={naLabel}>Направления <Text style={{ color: ACCENT_INK }}>*</Text></Text>
            <Text style={naHint}>Выберите одно или несколько — событие появится во всех соответствующих фильтрах календаря.</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                {directionOptions.map((d, i) => {
                    const active = directions.includes(d);
                    return (
                        <TouchableOpacity
                            key={d}
                            testID={`offline-event-direction-${i}`}
                            accessibilityRole="button"
                            accessibilityLabel={d}
                            accessibilityState={{ selected: active }}
                            onPress={() => toggleDirection(d)}
                            activeOpacity={0.85}
                            style={{ paddingHorizontal: 14, paddingVertical: 9, borderRadius: 999, backgroundColor: active ? ACCENT : 'transparent', borderWidth: 1, borderColor: ACCENT }}
                        >
                            <Text accessible={false} style={{ color: active ? ON_ACCENT_TEXT : tc.text, fontSize: 13, fontWeight: fontWeights.bold }}>{d}</Text>
                        </TouchableOpacity>
                    );
                })}
            </View>

            {/* Visibility */}
            <Text style={naLabel}>Кто видит</Text>
            <View style={{ flexDirection: 'row', padding: 4, borderRadius: 14, backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)', borderWidth: 1, borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', marginBottom: 8 }}>
                <TouchableOpacity
                    testID="offline-event-visibility-public"
                    accessibilityRole="button"
                    accessibilityLabel="Публичное"
                    onPress={() => setPublishToFeed(true)}
                    activeOpacity={0.85}
                    style={{ flex: 1, height: 42, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, borderRadius: 10, backgroundColor: publishToFeed ? '#10B981' : 'transparent' }}
                >
                    <Ionicons name="globe-outline" size={15} color={publishToFeed ? ON_ACCENT_TEXT : tc.textMuted} accessible={false} />
                    <Text accessible={false} style={{ color: publishToFeed ? ON_ACCENT_TEXT : tc.text, fontSize: 13, fontWeight: fontWeights.bold }}>Публичное</Text>
                </TouchableOpacity>
                <TouchableOpacity
                    testID="offline-event-visibility-private"
                    accessibilityRole="button"
                    accessibilityLabel="Приватное"
                    onPress={() => setPublishToFeed(false)}
                    activeOpacity={0.85}
                    style={{ flex: 1, height: 42, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, borderRadius: 10, backgroundColor: !publishToFeed ? ACCENT : 'transparent' }}
                >
                    <Ionicons name="lock-closed-outline" size={15} color={!publishToFeed ? ON_ACCENT_TEXT : tc.textMuted} accessible={false} />
                    <Text accessible={false} style={{ color: !publishToFeed ? ON_ACCENT_TEXT : tc.text, fontSize: 13, fontWeight: fontWeights.bold }}>Приватное</Text>
                </TouchableOpacity>
            </View>
            <Text style={naHint}>
                {publishToFeed ? 'Событие появится в календаре и в поиске. Любой пользователь сможет его найти.' : 'Событие скрыто из всех листингов. Откроется только по приватной ссылке.'}
            </Text>

            {/* Cover */}
            <Text style={naLabel}>Обложка</Text>
            <View style={{ width: '100%', aspectRatio: 16 / 9, borderRadius: 14, overflow: 'hidden', backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', borderWidth: 1, borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', marginBottom: 10, alignItems: 'center', justifyContent: 'center' }}>
                {coverPreview ? (
                    <Image source={{ uri: coverPreview }} style={{ width: '100%', height: '100%' }} />
                ) : (
                    <View style={{ alignItems: 'center', gap: 8 }}>
                        <Ionicons name="image-outline" size={30} color={tc.textMuted} />
                        <Text style={{ fontSize: 13, color: tc.textMuted }}>Выберите обложку ниже</Text>
                    </View>
                )}
            </View>
            {coversLoading ? (
                <View style={{ paddingVertical: 16, alignItems: 'center' }}><ActivityIndicator size="small" color={ACCENT_INK} /></View>
            ) : unsplashCovers.length > 0 ? (
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                    {unsplashCovers.slice(0, 8).map((img) => {
                        const selected = coverPreview === unsplashPhotoUrl(img.id, 'regular');
                        return (
                            <TouchableOpacity key={img.id} onPress={() => handlePickUnsplash(img)} disabled={!!pickingCoverId} activeOpacity={0.85} style={{ width: '23%', aspectRatio: 16 / 9, borderRadius: 8, overflow: 'hidden', borderWidth: 2, borderColor: selected ? ACCENT : 'transparent' }}>
                                <Image source={{ uri: unsplashPhotoUrl(img.id, 'small') }} style={{ width: '100%', height: '100%' }} />
                                {pickingCoverId === img.id && (
                                    <View style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.35)' }}>
                                        <ActivityIndicator size="small" color="#fff" />
                                    </View>
                                )}
                            </TouchableOpacity>
                        );
                    })}
                </View>
            ) : null}
            {coverStatus}
            <TouchableOpacity onPress={handlePickCover} activeOpacity={0.85} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 12, paddingHorizontal: 14, borderRadius: 12, backgroundColor: isDark ? 'rgba(233, 130, 69,0.08)' : 'rgba(233, 130, 69,0.06)', borderWidth: 1, borderColor: 'rgba(233, 130, 69,0.25)', borderStyle: 'dashed' }}>
                <Ionicons name="cloud-upload-outline" size={18} color={ACCENT_INK} />
                <Text style={{ flex: 1, fontSize: 14, color: ACCENT_INK, fontWeight: fontWeights.bold }}>Загрузить свою картинку</Text>
            </TouchableOpacity>

            {error && <Text testID="offline-event-error" style={{ color: REQUIRED_INK, fontSize: 13, marginTop: 14 }}>{error}</Text>}

            <TouchableOpacity
                testID="offline-event-preview"
                accessibilityRole="button"
                accessibilityLabel="Просмотр анонса"
                onPress={handleGoToPreview}
                activeOpacity={0.9}
                style={{ marginTop: 22, height: 52, borderRadius: 999, backgroundColor: canPreview ? ACCENT : tc.border, alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: 8 }}
            >
                <Ionicons name="eye-outline" size={18} color={canPreview ? ON_ACCENT_TEXT : tc.textMuted} accessible={false} />
                <Text accessible={false} style={{ color: canPreview ? ON_ACCENT_TEXT : tc.textMuted, fontSize: 15, fontWeight: fontWeights.bold }}>Просмотр анонса</Text>
            </TouchableOpacity>
            {!canPreview && (
                <Text style={[naHint, { textAlign: 'center', marginTop: 8 }]}>Чтобы продолжить: название (мин. 3 символа), адрес, направление.</Text>
            )}
        </>
    );

    const FlowRoot: any = screenMode ? View : Modal;

    return (
        <FlowRoot {...(!screenMode ? { visible, transparent: true, animationType: 'fade', onRequestClose: handleClose } : { style: { flex: 1 } })}>
            <View
                testID="offline-event-modal"
                accessibilityLabel="Офлайн-событие"
                style={NATIVE
                ? { flex: 1, backgroundColor: tc.canvas, paddingTop: insets.top }
                // CORE-1733 — страница создания события (screenMode) лежит на общем фоне,
                // а не на сплошном белом; окно поверх страницы — как было.
                : { flex: 1, minWidth: 0, minHeight: 0, backgroundColor: screenMode ? tc.pageBg : 'rgba(0,0,0,0.55)', alignItems: 'center', justifyContent: screenMode ? 'flex-start' : 'center', padding: screenMode ? 0 : 16 }}>
                <View style={NATIVE
                    ? { flex: 1, width: '100%', backgroundColor: tc.canvas }
                    : { width: '100%', maxWidth: screenMode ? 1240 / webHeightScale : 880, flex: screenMode ? 1 : undefined, minHeight: 0, maxHeight: screenMode ? undefined : '92%', ...(screenMode ? { backgroundColor: tc.pageBg } : panelFill), borderRadius: screenMode ? 0 : 22, overflow: 'hidden', ...(Platform.OS === 'web' && screenMode && webHeightScale < 1 && { zoom: webHeightScale } as any), ...(Platform.OS === 'web' && !screenMode && { boxShadow: '0 20px 60px rgba(0,0,0,0.25)' } as any) }}>
                    {NATIVE ? (
                        <View style={{ height: 48, flexDirection: 'row', alignItems: 'center', paddingHorizontal: 8 }}>
                            <TouchableOpacity
                                testID="offline-event-back"
                                accessibilityRole="button"
                                accessibilityLabel={step === 'preview' ? 'Назад' : 'Закрыть'}
                                hitSlop={8}
                                onPress={step === 'preview' ? () => setStep('edit') : handleClose}
                                style={{ width: 40, height: 40, alignItems: 'center', justifyContent: 'center' }}
                            >
                                <Ionicons name="chevron-back" size={26} color={tc.text} accessible={false} />
                            </TouchableOpacity>
                            <Text
                                testID={step === 'edit' ? 'offline-event-step-edit' : step === 'preview' ? 'offline-event-step-preview' : 'offline-event-step-success'}
                                style={{ flex: 1, textAlign: 'center', fontSize: 17, fontWeight: fontWeights.bold, color: tc.text } as any}
                                numberOfLines={1}
                            >
                                {isEdit ? 'Редактировать событие' : 'Офлайн-событие'}
                            </Text>
                            <View style={{ width: 40 }} />
                        </View>
                    ) : (
                        <View style={{ alignItems: screenMode ? 'center' : 'stretch', paddingHorizontal: screenMode ? (compactWeb ? 16 : 32) : 26, paddingTop: screenMode ? (shortWeb ? 14 : compactWeb ? 18 : 34) : 24, paddingBottom: screenMode ? (shortWeb ? 12 : compactWeb ? 20 : 28) : 14, position: 'relative' }}>
                            {screenMode && (
                                <TouchableOpacity onPress={handleClose} style={{ position: 'absolute', top: shortWeb ? 10 : compactWeb ? 14 : 34, left: compactWeb ? 16 : 0, zIndex: 2, flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 8 }}>
                                    <Ionicons name="exit-outline" size={20} color={tc.textMuted} />
                                    {!compactWeb && <Text style={{ fontFamily: 'Nunito Sans', fontSize: 14, lineHeight: 20, fontWeight: '600', color: tc.textMuted }}>Выйти</Text>}
                                </TouchableOpacity>
                            )}
                            <View style={{ width: '100%', alignItems: screenMode ? 'center' : 'stretch' }}>
                                {screenMode && (
                                    <Text style={{ fontFamily: 'Nunito Sans', fontSize: 14, lineHeight: 20, fontWeight: '600', color: ACCENT_INK, marginBottom: 6 } as any}>
                                        Создание офлайн-события · {step === 'edit' ? '1 из 3' : step === 'design' ? '2 из 3' : '3 из 3'}
                                    </Text>
                                )}
                                <Text style={{ fontFamily: screenMode ? 'Nunito Sans' : undefined, fontSize: screenMode ? (compactWeb || shortWeb ? 28 : 40) : 24, lineHeight: screenMode ? (compactWeb || shortWeb ? 36 : 48) : undefined, fontWeight: screenMode ? '700' : '800', color: tc.text, textAlign: screenMode ? 'center' : 'left', paddingHorizontal: compactWeb ? 32 : 0 } as any}>
                                    {isEdit ? 'Редактировать офлайн' : step === 'edit' ? 'Создайте пространство для вашей встречи' : step === 'design' ? 'Добавьте оформление события' : step === 'preview' ? 'Проверьте анонс перед публикацией' : 'Событие готово'}
                                </Text>
                                {screenMode && step !== 'success' && !shortWeb && <Text style={{ fontFamily: 'Nunito Sans', fontSize: compactWeb ? 16 : 18, lineHeight: compactWeb ? 24 : 28, color: tc.textMuted, marginTop: 4, textAlign: 'center' } as any}>{step === 'edit' ? 'Добавьте основные детали будущей встречи.' : step === 'design' ? 'Выберите обложку и проверьте карточку события.' : 'Проверьте анонс и добавьте личное приглашение в Ленту.'}</Text>}
                                {screenMode && step !== 'success' && (
                                    <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: shortWeb ? 10 : 24 }}>
                                        {(['edit', 'design', 'preview'] as const).map((key, index) => {
                                            const current = key === step;
                                            const stepIndex = step === 'edit' ? 0 : step === 'design' ? 1 : 2;
                                            const completed = stepIndex > index;
                                            const label = index === 0 ? 'Основное' : index === 1 ? 'Оформление' : 'Проверка';
                                            const circle = shortWeb ? 32 : 40;
                                            return <React.Fragment key={key}>{index > 0 && <View style={{ width: compactWeb ? 20 : 56, height: 1, backgroundColor: completed || current ? ACCENT : tc.border, marginHorizontal: compactWeb ? 6 : 12, marginBottom: shortWeb ? 20 : 26 }} />}<View style={{ alignItems: 'center', gap: shortWeb ? 2 : 6 }}><View style={{ width: circle, height: circle, borderRadius: circle / 2, backgroundColor: current ? ACCENT : 'transparent', borderWidth: 1, borderColor: completed || current ? ACCENT : tc.border, alignItems: 'center', justifyContent: 'center' }}>{completed ? <Ionicons name="checkmark" size={16} color={current ? ON_ACCENT_TEXT : ACCENT_INK} /> : <Text style={{ color: current ? ON_ACCENT_TEXT : tc.textMuted, fontFamily: 'Nunito Sans', fontSize: 14, lineHeight: 20, fontWeight: fontWeights.bold }}>{index + 1}</Text>}</View><Text style={{ color: current ? tc.text : tc.textMuted, fontFamily: 'Nunito Sans', fontSize: compactWeb ? 12 : 14, lineHeight: compactWeb ? 16 : 20, fontWeight: '600' }}>{label}</Text></View></React.Fragment>;
                                        })}
                                    </View>
                                )}
                            </View>
                            {!screenMode && <TouchableOpacity onPress={handleClose} style={{ position: 'absolute', right: 26, top: 24, width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center' }}>
                                <Ionicons name="close" size={22} color={tc.text} />
                            </TouchableOpacity>}
                        </View>
                    )}

                    <ScrollView style={{ flex: 1, minHeight: 0, paddingHorizontal: NATIVE ? 16 : (screenMode ? (compactWeb ? 16 : 32) : 26) }} contentContainerStyle={{ paddingBottom: NATIVE ? insets.bottom + 24 : (screenMode ? (shortWeb ? 12 : 40) : 26) }} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
                        {(step === 'edit' || step === 'design') && (NATIVE ? renderNativeEdit() : (
                            <>
                                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 24 } as any}>
                                    <View style={{ flex: 1, minWidth: compactWeb ? 0 : 320, width: compactWeb ? '100%' : undefined } as any}>
                                        {step === 'edit' && <View style={screenMode ? { padding: shortWeb ? 16 : 22, borderRadius: 20, ...panelFill, marginBottom: shortWeb ? 8 : 14 } : undefined}>
                                        {screenMode && <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: shortWeb ? 10 : 18 }}><View style={{ width: 34, height: 34, borderRadius: 17, backgroundColor: isDark ? 'rgba(233,130,69,0.14)' : 'rgba(233,130,69,0.10)', alignItems: 'center', justifyContent: 'center' }}><Ionicons name="people-outline" size={17} color={ACCENT_INK} /></View><Text style={{ fontFamily: 'Nunito Sans', fontSize: 16, lineHeight: 24, fontWeight: fontWeights.bold, color: tc.text }}>Основная информация</Text></View>}
                                        <Text style={fieldLabel(tc.text) as any}>Название события <Text style={{ color: REQUIRED_INK }}>*</Text></Text>
                                        <TextInput testID="offline-event-title-input" accessibilityLabel="Название события" value={title} onChangeText={setTitle} placeholder="напр. Утренняя медитация на пляже" placeholderTextColor={tc.textMuted} style={[inputStyle(isDark, tc), shortWeb && { paddingVertical: 11 }, { marginBottom: shortWeb ? 10 : 18 }] as any} />

                                        <Text style={fieldLabel(tc.text) as any}>Адрес <Text style={{ color: REQUIRED_INK }}>*</Text></Text>
                                        <TextInput testID="offline-event-address-input" accessibilityLabel="Адрес" value={address} onChangeText={setAddress} editable={!lockTimePlace} placeholder="Город, улица, дом, ориентир" placeholderTextColor={tc.textMuted} style={[inputStyle(isDark, tc), shortWeb && { paddingVertical: 11 }, { marginBottom: 4 }, lockTimePlace && { opacity: 0.55 }] as any} />
                                        <Text style={{ fontSize: 12, color: tc.textMuted, marginBottom: shortWeb ? 8 : 18, lineHeight: 17 } as any}>
                                            {lockTimePlace
                                                ? 'Событие уже прошло — время и место изменить нельзя.'
                                                : 'Карта-метка будет добавлена в одном из следующих обновлений.'}
                                        </Text>

                                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12, rowGap: shortWeb ? 8 : 14, marginBottom: shortWeb ? 8 : 18 } as any}>
                                            <View style={{ flex: 1, minWidth: 150 } as any}>
                                                <Text style={fieldLabel(tc.text) as any}>Дата</Text>
                                                {Platform.OS === 'web' ? (
                                                    <input type="date" data-testid="offline-event-date" min={todayISO()} value={date} disabled={lockTimePlace} onInput={handleWebDateInput} style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${tc.border}`, borderRadius: 14, padding: shortWeb ? '11px 14px' : '18px 18px', fontSize: 16, fontWeight: 600, fontFamily: 'Nunito Sans', color: tc.text, backgroundColor: nestedSurface, cursor: lockTimePlace ? 'not-allowed' : 'pointer', opacity: lockTimePlace ? 0.55 : 1 } as any} />
                                                ) : (
                                                    <DatePickerField value={date} onChange={setDate} minDate={todayISO()} disabled={lockTimePlace} />
                                                )}
                                            </View>
                                            <View style={{ width: 130 }}>
                                                <Text style={fieldLabel(tc.text) as any}>Время</Text>
                                                {Platform.OS === 'web' ? (
                                                    <input type="time" data-testid="offline-event-time" value={time} disabled={lockTimePlace} onInput={handleWebTimeInput} style={{ width: '100%', boxSizing: 'border-box', border: `1px solid ${tc.border}`, borderRadius: 14, padding: shortWeb ? '11px 14px' : '18px 18px', fontSize: 16, fontWeight: 600, fontFamily: 'Nunito Sans', color: tc.text, backgroundColor: nestedSurface, cursor: lockTimePlace ? 'not-allowed' : 'pointer', opacity: lockTimePlace ? 0.55 : 1 } as any} />
                                                ) : (
                                                    <TimePickerField value={time} onChange={setTime} disabled={lockTimePlace} />
                                                )}
                                            </View>
                                            {/* CORE-225 — numeric value + unit selector. Days/hours
                                                enable multi-day offline events (retreats, festivals). */}
                                            <View style={{ flexGrow: 1, minWidth: 190 } as any}>
                                                <Text style={fieldLabel(tc.text) as any}>Длительность</Text>
                                                <View style={{ flexDirection: 'row', gap: 8 }}>
                                                    <TextInput testID="offline-event-duration-input" value={duration} onChangeText={(v) => setDuration(v.replace(/[^0-9]/g, ''))} editable={!lockTimePlace} keyboardType="numeric" style={[inputStyle(isDark, tc), { width: 68, paddingVertical: shortWeb ? 11 : 18, paddingHorizontal: 14, fontSize: 17, fontWeight: '600', borderRadius: 14, textAlign: 'center' }, lockTimePlace && { opacity: 0.55 }] as any} />
                                                    {Platform.OS === 'web' ? (
                                                        <select
                                                            data-testid="offline-event-duration-unit"
                                                            value={durationUnit}
                                                            disabled={lockTimePlace}
                                                            onChange={(e: any) => setDurationUnit(e.target.value as DurationUnit)}
                                                            style={{ flex: 1, boxSizing: 'border-box', border: `1px solid ${tc.border}`, borderRadius: 14, padding: shortWeb ? '11px 14px' : '18px 14px', fontSize: 16, fontWeight: 600, fontFamily: 'Nunito Sans', color: tc.text, backgroundColor: nestedSurface, cursor: lockTimePlace ? 'not-allowed' : 'pointer', opacity: lockTimePlace ? 0.55 : 1 } as any}
                                                        >
                                                            {DURATION_UNIT_ORDER.map((u) => (
                                                                <option key={u} value={u}>{DURATION_UNIT_LABEL[u]}</option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <TouchableOpacity
                                                            testID="offline-event-duration-unit"
                                                            onPress={() => {
                                                                if (lockTimePlace) return;
                                                                const idx = DURATION_UNIT_ORDER.indexOf(durationUnit);
                                                                setDurationUnit(DURATION_UNIT_ORDER[(idx + 1) % DURATION_UNIT_ORDER.length]);
                                                            }}
                                                            activeOpacity={0.85}
                                                            style={[inputStyle(isDark, tc), { flex: 1, paddingVertical: 18, paddingHorizontal: 14, borderRadius: 14, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }, lockTimePlace && { opacity: 0.55 }] as any}
                                                        >
                                                            <Text style={{ fontSize: 17, fontWeight: '600', color: tc.text } as any}>{DURATION_UNIT_LABEL[durationUnit]}</Text>
                                                            <Ionicons name="chevron-expand" size={16} color={tc.textMuted} />
                                                        </TouchableOpacity>
                                                    )}
                                                </View>
                                            </View>
                                        </View>

                                        {error?.startsWith('Время начала уже прошло') && (
                                            <Text style={{ color: REQUIRED_INK, fontFamily: 'Nunito Sans', fontSize: 12, lineHeight: 16, fontWeight: '600', marginBottom: shortWeb ? 6 : 12 } as any}>
                                                {error}
                                            </Text>
                                        )}

                                        <Text style={fieldLabel(tc.text) as any}>Краткое описание</Text>
                                        <TextInput testID="offline-event-subtitle-input" value={subtitle} onChangeText={setSubtitle} maxLength={SHORT_DESCRIPTION_MAX} placeholder="Коротко о событии — этот текст появится в карточке" placeholderTextColor={tc.textMuted} multiline style={[inputStyle(isDark, tc), { minHeight: shortWeb ? 44 : 70, textAlignVertical: 'top', paddingVertical: shortWeb ? 9 : undefined }] as any} />
                                        <Text style={{ color: tc.textMuted, fontFamily: 'Nunito Sans', fontSize: 12, lineHeight: 16, textAlign: 'right', marginTop: 3, marginBottom: shortWeb ? 6 : 12 } as any}>{subtitle.length} / {SHORT_DESCRIPTION_MAX}</Text>

                                        <Text style={fieldLabel(tc.text) as any}>Подробнее</Text>
                                        <TextInput testID="offline-event-description-input" value={description} onChangeText={setDescription} maxLength={DETAILS_MAX} placeholder="Расскажите о программе, подготовке и важных деталях" placeholderTextColor={tc.textMuted} multiline style={[inputStyle(isDark, tc), { minHeight: shortWeb ? 52 : 90, textAlignVertical: 'top', paddingVertical: shortWeb ? 9 : undefined }] as any} />
                                        <Text style={{ color: tc.textMuted, fontFamily: 'Nunito Sans', fontSize: 12, lineHeight: 16, textAlign: 'right', marginTop: 3, marginBottom: shortWeb ? 0 : 6 } as any}>{description.length} / {DETAILS_MAX}</Text>
                                        </View>}

                                        {step === 'edit' && <View style={screenMode ? { padding: shortWeb ? 14 : 22, borderRadius: 20, ...panelFill } : undefined}>
                                        <Text style={fieldLabel(tc.text) as any}>Направления <Text style={{ color: REQUIRED_INK }}>*</Text></Text>
                                        {!shortWeb && <Text style={{ fontSize: 12, color: tc.textMuted, marginTop: -4, marginBottom: 8, lineHeight: 17 } as any}>
                                            Выберите одно или несколько — событие появится во всех соответствующих фильтрах календаря.
                                        </Text>}
                                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: shortWeb ? 6 : 8, marginBottom: shortWeb ? 10 : 18 }}>
                                            {directionOptions.map((d) => {
                                                const active = directions.includes(d);
                                                return (
                                                    <TouchableOpacity
                                                        key={d}
                                                        onPress={() => toggleDirection(d)}
                                                        activeOpacity={0.85}
                                                        style={{ paddingHorizontal: shortWeb ? 11 : 14, paddingVertical: shortWeb ? 6 : 8, borderRadius: 999, backgroundColor: active ? ACCENT : 'transparent', borderWidth: 1, borderColor: active ? ACCENT : (isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.12)'), ...(Platform.OS === 'web' && { cursor: 'pointer' } as any) } as any}
                                                    >
                                                        <Text style={{ color: active ? ON_ACCENT_TEXT : tc.text, fontSize: 13, fontWeight: fontWeights.bold } as any}>{d}</Text>
                                                    </TouchableOpacity>
                                                );
                                            })}
                                        </View>
                                        {error?.startsWith('Выберите хотя бы одно направление') && (
                                            <Text style={{ color: REQUIRED_INK, fontFamily: 'Nunito Sans', fontSize: 12, lineHeight: 16, fontWeight: '600', marginTop: -10, marginBottom: 16 } as any}>
                                                Выберите хотя бы одно направление
                                            </Text>
                                        )}

                                        {/* Visibility — segmented selector for offline events
                                            (mirrors the LiveStream modal). */}
                                        <Text style={{ fontSize: 12, fontWeight: fontWeights.bold, color: tc.textMuted, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 8 } as any}>
                                            Видимость
                                        </Text>
                                        <View style={{
                                            flexDirection: 'row',
                                            padding: 4,
                                            borderRadius: 14,
                                            backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
                                            borderWidth: 1,
                                            borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                                            marginBottom: 8,
                                        }}>
                                            <TouchableOpacity
                                                onPress={() => setPublishToFeed(true)}
                                                activeOpacity={0.85}
                                                style={{
                                                    flex: 1, height: shortWeb ? 34 : 40, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
                                                    gap: 6, borderRadius: 10,
                                                    backgroundColor: publishToFeed ? ACCENT : 'transparent',
                                                    ...(Platform.OS === 'web' && { cursor: 'pointer' } as any),
                                                } as any}
                                            >
                                                <Ionicons name="globe-outline" size={15} color={publishToFeed ? ON_ACCENT_TEXT : tc.textMuted} />
                                                <Text style={{ color: publishToFeed ? ON_ACCENT_TEXT : tc.text, fontSize: 13, fontWeight: fontWeights.bold } as any}>Публичное</Text>
                                            </TouchableOpacity>
                                            <TouchableOpacity
                                                onPress={() => setPublishToFeed(false)}
                                                activeOpacity={0.85}
                                                style={{
                                                    flex: 1, height: shortWeb ? 34 : 40, flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
                                                    gap: 6, borderRadius: 10,
                                                    backgroundColor: !publishToFeed ? '#E98245' : 'transparent',
                                                    ...(Platform.OS === 'web' && { cursor: 'pointer' } as any),
                                                } as any}
                                            >
                                                <Ionicons name="lock-closed-outline" size={15} color={!publishToFeed ? ON_ACCENT_TEXT : tc.textMuted} />
                                                <Text style={{ color: !publishToFeed ? ON_ACCENT_TEXT : tc.text, fontSize: 13, fontWeight: fontWeights.bold } as any}>Приватное</Text>
                                            </TouchableOpacity>
                                        </View>
                                        {!shortWeb && <Text style={{ fontSize: 12, color: tc.textMuted, lineHeight: 17 } as any}>
                                            {publishToFeed
                                                ? 'Событие появится в календаре и в поиске. Любой пользователь сможет его найти.'
                                                : 'Событие скрыто из всех листингов. Откроется только у тех, кому вы отправите приватную ссылку.'}
                                        </Text>}
                                        </View>}
                                    </View>

                                    {(step === 'design' || (!screenMode && step === 'edit')) && <View style={{ flex: 1, minWidth: compactWeb ? 0 : 300, width: compactWeb ? '100%' : undefined } as any}>
                                        <View style={screenMode ? { padding: shortWeb ? 14 : 22, borderRadius: 20, ...panelFill } : undefined}>
                                        <Text style={fieldLabel(tc.text) as any}>Обложка события</Text>
                                        {screenMode && !shortWeb && <Text style={{ fontFamily: 'Nunito Sans', fontSize: 12, lineHeight: 16, color: tc.textMuted, marginTop: -4, marginBottom: 12 }}>Выберите изображение, которое передаст атмосферу</Text>}
                                        {!shortWeb && <View style={{ width: '100%', aspectRatio: 16 / 9, borderRadius: 14, overflow: 'hidden', backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', borderWidth: 1, borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', marginBottom: 12, alignItems: 'center', justifyContent: 'center' } as any}>
                                            {coverPreview ? (
                                                <Image source={{ uri: coverPreview }} resizeMode="cover" style={{ width: '100%', height: '100%' } as any} />
                                            ) : (
                                                <View style={{ alignItems: 'center', gap: 8 }}>
                                                    <Ionicons name="image-outline" size={32} color={tc.textMuted} />
                                                    <Text style={{ fontSize: 14, color: tc.textMuted } as any}>Выберите обложку ниже</Text>
                                                </View>
                                            )}
                                        </View>}

                                        {coversLoading ? (
                                            <View style={{ paddingVertical: 16, alignItems: 'center' }}>
                                                <ActivityIndicator size="small" color={ACCENT_INK} />
                                            </View>
                                        ) : unsplashCovers.length > 0 ? (
                                            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                                                {unsplashCovers.slice(0, shortWeb ? 4 : 8).map((img) => {
                                                    const selected = coverPreview === unsplashPhotoUrl(img.id, 'regular');
                                                    return (
                                                        <TouchableOpacity
                                                            key={img.id}
                                                            testID={`offline-event-cover-${img.id}`}
                                                            onPress={() => handlePickUnsplash(img)}
                                                            disabled={!!pickingCoverId}
                                                            activeOpacity={0.85}
                                                            style={{ width: 'calc(25% - 6px)' as any, aspectRatio: 16 / 9, borderRadius: 8, overflow: 'hidden', borderWidth: 2, borderColor: selected ? ACCENT : 'transparent', ...(Platform.OS === 'web' && { cursor: 'pointer' } as any) } as any}
                                                        >
                                                            <Image source={{ uri: unsplashPhotoUrl(img.id, 'small') }} style={{ width: '100%', height: '100%' } as any} />
                                                            {pickingCoverId === img.id && (
                                                                <View style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.35)' }}>
                                                                    <ActivityIndicator size="small" color="#fff" />
                                                                </View>
                                                            )}
                                                        </TouchableOpacity>
                                                    );
                                                })}
                                            </View>
                                        ) : null}
                                        {coverStatus}

                                        <TouchableOpacity onPress={handlePickCover} activeOpacity={0.85} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 12, paddingHorizontal: 14, borderRadius: 12, backgroundColor: isDark ? 'rgba(233, 130, 69,0.08)' : 'rgba(233, 130, 69,0.06)', borderWidth: 1, borderColor: 'rgba(233, 130, 69,0.25)', borderStyle: 'dashed', marginBottom: 6, ...(Platform.OS === 'web' && { cursor: 'pointer' } as any) } as any}>
                                            <Ionicons name="cloud-upload-outline" size={18} color={ACCENT_INK} />
                                            <Text style={{ flex: 1, fontSize: 14, color: ACCENT_INK, fontWeight: fontWeights.bold } as any}>Загрузить свою картинку</Text>
                                        </TouchableOpacity>

                                        {screenMode && (
                                            <View style={{ marginTop: shortWeb ? 10 : 22 }}>
                                                <Text style={{ fontFamily: 'Nunito Sans', fontSize: 14, lineHeight: 20, fontWeight: '600', color: tc.text, marginBottom: 10 } as any}>
                                                    Как увидят ваше событие пользователи
                                                </Text>
                                                <View style={{ borderRadius: 18, overflow: 'hidden', borderWidth: 1, borderColor: tc.border, backgroundColor: 'transparent', flexDirection: horizontalPreview ? 'row' : 'column', alignItems: horizontalPreview ? 'stretch' : undefined } as any}>
                                                    {coverPreview && <Image source={{ uri: coverPreview }} resizeMode="cover" style={{ width: horizontalPreview ? '38%' : '100%', aspectRatio: 16 / 9, alignSelf: 'center', backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' } as any} />}
                                                    <View style={{ flex: horizontalPreview ? 1 : undefined, padding: shortWeb ? 10 : 16, gap: shortWeb ? 3 : 8, justifyContent: horizontalPreview ? 'center' : undefined } as any}>
                                                        <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
                                                            <View style={{ backgroundColor: ACCENT, borderRadius: 999, paddingHorizontal: 10, paddingVertical: 4 }}>
                                                                <Text style={{ color: ON_ACCENT_TEXT, fontFamily: 'Nunito Sans', fontSize: 12, lineHeight: 16 }}>Офлайн</Text>
                                                            </View>
                                                            <View style={{ borderColor: ACCENT, borderWidth: 1, borderRadius: 999, paddingHorizontal: 10, paddingVertical: 3 }}>
                                                                <Text style={{ color: ACCENT_INK, fontFamily: 'Nunito Sans', fontSize: 12, lineHeight: 16 }}>{publishToFeed ? 'Публичное' : 'Приватное'}</Text>
                                                            </View>
                                                        </View>
                                                        <Text style={{ color: tc.text, fontFamily: 'Nunito Sans', fontSize: shortWeb ? 16 : 22, lineHeight: shortWeb ? 20 : 28, fontWeight: '600' } as any}>{title.trim() || 'Название события'}</Text>
                                                        <Text style={{ color: tc.textMuted, fontFamily: 'Nunito Sans', fontSize: 14, lineHeight: 20 } as any}>{formatPreviewDate(date, time)} · {formatEventDuration(durationToMinutes(parseInt(duration, 10) || 0, durationUnit) || 60)}</Text>
                                                        <Text style={{ color: tc.textMuted, fontFamily: 'Nunito Sans', fontSize: 14, lineHeight: 20 } as any}>{address.trim() || 'Адрес появится здесь'}</Text>
                                                        {directions.length > 0 && <Text style={{ color: tc.text, fontFamily: 'Nunito Sans', fontSize: 14, lineHeight: 20 } as any}>{directions.join(' · ')}</Text>}
                                                    </View>
                                                </View>
                                            </View>
                                        )}
                                        </View>
                                    </View>}
                                </View>

                                {error && !error.startsWith('Выберите хотя бы одно направление') && !error.startsWith('Время начала уже прошло') && (
                                    <Text style={{ color: REQUIRED_INK, fontSize: 13, marginTop: 14, marginBottom: 6 } as any}>{error}</Text>
                                )}

                                {/* AMB-197 — no `disabled` here. Button stays clickable so
                                    handleGoToPreview can surface a field-specific error;
                                    visual «disabled» state remains via primaryBtn styling. */}
                                <View style={screenMode ? { marginTop: shortWeb ? 10 : 22, padding: shortWeb || compactWeb ? 10 : 16, borderRadius: 18, ...panelFill, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 } : undefined}>
                                    {screenMode && step === 'design' && <TouchableOpacity testID="offline-event-design-back" onPress={() => setStep(previousOfflineEventStep(step, screenMode))} disabled={publishing} style={{ height: 52, paddingHorizontal: 20, borderRadius: 14, borderWidth: 1, borderColor: tc.border, alignItems: 'center', justifyContent: 'center' }}><Text style={{ color: tc.text, fontFamily: 'Nunito Sans', fontSize: 16, lineHeight: 24, fontWeight: '600' }}>Назад</Text></TouchableOpacity>}
                                    {!isEdit && <TouchableOpacity onPress={handleSaveDraft} disabled={publishing} style={{ height: 52, paddingHorizontal: 20, borderRadius: 14, borderWidth: 1, borderColor: tc.border, alignItems: 'center', justifyContent: 'center' }}><Text style={{ color: tc.text, fontFamily: 'Nunito Sans', fontSize: 16, lineHeight: 24, fontWeight: '600' }}>Сохранить черновик</Text></TouchableOpacity>}
                                    {/* CORE-1733 (возврат QA 23.10) — как «Продолжить» мастера Live: оранжевая CTA
                                        с тёмной подписью. Пока поля не заполнены — тот же оранжевый контуром, а не
                                        холодный #CBD5E0 с белым (1.49:1): кнопка нажимается и показывает ошибку
                                        (AMB-197), значит её подпись обязана читаться. */}
                                    <TouchableOpacity testID="offline-event-next" onPress={handleGoToPreview} activeOpacity={0.85} style={[primaryBtn(!canPreview, isDark), { backgroundColor: canPreview ? ACCENT : 'transparent', borderWidth: 1.5, borderColor: ACCENT }, screenMode && { width: compactWeb ? undefined : 264, flex: compactWeb ? 1 : undefined, marginTop: 0 }, { flexDirection: 'row', justifyContent: 'center', gap: 10 }] as any}>
                                        <Text style={{ color: canPreview ? ON_ACCENT_TEXT : ACCENT_INK, fontFamily: 'Nunito Sans', fontSize: 16, lineHeight: 24, fontWeight: fontWeights.bold } as any}>{screenMode && step === 'edit' ? 'К оформлению' : 'К проверке'}</Text>
                                        <Ionicons name="arrow-forward" size={18} color={canPreview ? ON_ACCENT_TEXT : ACCENT_INK} />
                                    </TouchableOpacity>
                                </View>
                                {screenMode && !shortWeb && <Text style={{ marginTop: 12, textAlign: 'center', color: tc.textMuted, fontFamily: 'Nunito Sans', fontSize: 12, lineHeight: 16 } as any}>{step === 'edit' ? 'Далее: оформление события' : 'Далее: проверка и публикация'}</Text>}
                            </>
                        ))}

                        {step === 'preview' && (
                            <View style={screenMode ? { width: '100%', maxWidth: 920, alignSelf: 'center' } : undefined}>

                                <View style={{ borderRadius: 18, overflow: 'hidden', ...(NATIVE ? { borderWidth: 1, borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)', backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : '#FFF' } : panelFill), marginBottom: shortWeb ? 10 : 22, flexDirection: horizontalPreview ? 'row' : 'column', alignItems: horizontalPreview ? 'stretch' : undefined } as any}>
                                    <View style={{ width: horizontalPreview ? '42%' : '100%', aspectRatio: screenMode ? (horizontalPreview ? 16 / 9 : 3.2) : 16 / 9, backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)', position: 'relative', alignSelf: horizontalPreview ? 'center' : undefined } as any}>
                                        {coverPreview ? (
                                            <Image source={{ uri: coverPreview }} resizeMode="cover" style={{ width: '100%', height: '100%' } as any} />
                                        ) : (
                                            <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                                                <Ionicons name="image-outline" size={36} color={tc.textMuted} />
                                            </View>
                                        )}
                                        <View style={{ position: 'absolute', top: 12, left: 12, flexDirection: 'row', gap: 6 }}>
                                            <View style={{ backgroundColor: ACCENT, paddingHorizontal: 10, paddingVertical: 5, borderRadius: 999 } as any}>
                                                <Text style={{ color: ON_ACCENT_TEXT, fontFamily: 'Nunito Sans', fontSize: 12, lineHeight: 16, fontWeight: '600' } as any}>Офлайн</Text>
                                            </View>
                                            {!publishToFeed && (
                                                <View style={{ backgroundColor: 'rgba(0,0,0,0.6)', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 999 } as any}>
                                                    <Text style={{ color: '#FFF', fontSize: 11, fontWeight: fontWeights.bold } as any}>ПРИВАТНОЕ</Text>
                                                </View>
                                            )}
                                        </View>
                                    </View>
                                    <View style={{ flex: horizontalPreview ? 1 : undefined, padding: shortWeb ? 10 : 18, justifyContent: horizontalPreview ? 'center' : undefined } as any}>
                                        <Text style={{ fontSize: shortWeb ? 18 : 22, fontWeight: fontWeights.bold, color: tc.text, marginBottom: shortWeb ? 4 : 8, letterSpacing: -0.3 } as any}>{title.trim() || 'Без названия'}</Text>
                                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: shortWeb ? 4 : 12, flexWrap: 'wrap' }}>
                                            <Ionicons name="location-outline" size={14} color={tc.textMuted} />
                                            <Text style={{ fontSize: 13, color: tc.text, fontWeight: '600' } as any}>{address.trim() || 'Адрес не указан'}</Text>
                                        </View>
                                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: shortWeb ? 4 : 14, flexWrap: 'wrap' }}>
                                            <Ionicons name="calendar-outline" size={14} color={tc.textMuted} />
                                            <Text style={{ fontSize: 13, color: tc.text, fontWeight: '600' } as any}>{formatPreviewDate(date, time)}</Text>
                                            <Text style={{ fontSize: 13, color: tc.textMuted } as any}>·</Text>
                                            <Text style={{ fontSize: 13, color: tc.text, fontWeight: '600' } as any}>{formatEventDuration(durationToMinutes(parseInt(duration, 10) || 0, durationUnit) || 60)}</Text>
                                            <Text style={{ fontSize: 13, color: tc.textMuted } as any}>·</Text>
                                            <Text style={{ fontSize: 13, color: tc.text, fontWeight: '600' } as any}>{masterName || 'Вы'}</Text>
                                        </View>
                                        {(subtitle.trim() || description.trim()) ? (
                                            <Text numberOfLines={shortWeb ? 1 : undefined} style={{ fontSize: 14, color: tc.text, opacity: 0.85, lineHeight: 21 } as any}>{subtitle.trim() || description.trim()}</Text>
                                        ) : (
                                            <Text style={{ fontSize: 13, color: tc.textMuted, fontStyle: 'italic' } as any}>Без описания</Text>
                                        )}
                                    </View>
                                </View>

                                {/* CORE-227 — feed announcement block. Only on
                                    create: editing an existing event doesn't
                                    re-post the announcement, so the field would
                                    be a dead input. */}
                                {!isEdit && (
                                    <>
                                        <Text style={fieldLabel(tc.text) as any}>Сообщение в вашу Ленту</Text>
                                        {!shortWeb && <Text style={{ fontSize: 12, color: tc.textMuted, marginBottom: 8, lineHeight: 17 } as any}>
                                            Это сообщение появится вместе с анонсом события в вашей Ленте. Добавьте личное приглашение для подписчиков.
                                        </Text>}
                                        <TextInput
                                            value={feedMessage}
                                            onChangeText={setFeedMessage}
                                            editable={publishAnnouncement}
                                            maxLength={FEED_MESSAGE_MAX}
                                            placeholder="Дорогие мои, приглашаю вас на…"
                                            placeholderTextColor={tc.textMuted}
                                            multiline
                                            style={[inputStyle(isDark, tc), { minHeight: shortWeb ? 46 : 70, paddingVertical: shortWeb ? 9 : undefined, textAlignVertical: 'top' }, !publishAnnouncement && { opacity: 0.5 }] as any}
                                        />
                                        <Text style={{ color: tc.textMuted, fontFamily: 'Nunito Sans', fontSize: 12, lineHeight: 16, textAlign: 'right', marginTop: 3, marginBottom: shortWeb ? 8 : 12, opacity: publishAnnouncement ? 1 : 0.5 } as any}>
                                            {feedMessage.length} / {FEED_MESSAGE_MAX}
                                        </Text>

                                        {/* CORE-227 — opt out of publishing the
                                            announcement to the master's feed. */}
                                        <TouchableOpacity
                                            onPress={() => setPublishAnnouncement(v => !v)}
                                            activeOpacity={0.8}
                                            style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: shortWeb ? 8 : 18, ...(Platform.OS === 'web' && { cursor: 'pointer' } as any) } as any}
                                        >
                                            <View style={{ width: 22, height: 22, borderRadius: 6, borderWidth: 2, borderColor: publishAnnouncement ? ACCENT : (isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.25)'), backgroundColor: publishAnnouncement ? ACCENT : 'transparent', alignItems: 'center', justifyContent: 'center' } as any}>
                                                {publishAnnouncement && <Ionicons name="checkmark" size={15} color={ON_ACCENT_TEXT} />}
                                            </View>
                                            <Text style={{ flex: 1, fontSize: 14, color: tc.text, fontWeight: '600' } as any}>Опубликовать анонс события в моей Ленте</Text>
                                        </TouchableOpacity>
                                    </>
                                )}

                                <View style={{ flexDirection: 'row', gap: 10, padding: shortWeb ? 8 : 14, borderRadius: 12, backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', marginBottom: shortWeb ? 8 : 18 } as any}>
                                    <Ionicons name="information-circle-outline" size={18} color={tc.textMuted} style={{ marginTop: 1 } as any} />
                                    <Text numberOfLines={shortWeb ? 2 : undefined} style={{ flex: 1, fontSize: 12, color: tc.text, opacity: 0.75, lineHeight: shortWeb ? 16 : 18 } as any}>{PUBLISH_DISCLAIMER}</Text>
                                </View>

                                {error && (
                                    <Text style={{ color: REQUIRED_INK, fontSize: 13, marginBottom: 10 } as any}>{error}</Text>
                                )}

                                {NATIVE ? (
                                    <View style={{ flexDirection: 'row', gap: 10 }}>
                                        <TouchableOpacity
                                            testID="offline-event-preview-back"
                                            accessibilityRole="button"
                                            accessibilityLabel="Назад"
                                            onPress={() => setStep('edit')}
                                            disabled={publishing}
                                            activeOpacity={0.85}
                                            style={{ height: 50, paddingHorizontal: 18, borderRadius: 14, borderWidth: 1, borderColor: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.12)', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6 } as any}
                                        >
                                            <Ionicons name="arrow-back" size={16} color={tc.text} accessible={false} />
                                            <Text accessible={false} style={{ color: tc.text, fontSize: 14, fontWeight: fontWeights.bold } as any}>Назад</Text>
                                        </TouchableOpacity>
                                        <TouchableOpacity
                                            testID="offline-event-publish"
                                            accessibilityRole="button"
                                            accessibilityLabel={isEdit ? 'Сохранить' : 'Опубликовать'}
                                            onPress={() => void handlePublish(false)}
                                            disabled={publishing}
                                            activeOpacity={0.85}
                                            style={{ flex: 1, height: 50, borderRadius: 14, backgroundColor: publishing ? (isDark ? 'rgba(233, 130, 69,0.32)' : '#FFC9A8') : ACCENT, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 } as any}
                                        >
                                            {publishing ? (
                                                <ActivityIndicator size="small" color={ON_ACCENT_TEXT} />
                                            ) : (
                                                <>
                                                    <Ionicons name={isEdit ? 'save-outline' : 'paper-plane'} size={17} color={ON_ACCENT_TEXT} accessible={false} />
                                                    <Text accessible={false} style={{ color: ON_ACCENT_TEXT, fontSize: 14, fontWeight: fontWeights.bold } as any} numberOfLines={1}>{isEdit ? 'Сохранить' : 'Опубликовать'}</Text>
                                                </>
                                            )}
                                        </TouchableOpacity>
                                    </View>
                                ) : (
                                    <View style={{ flexDirection: 'row', gap: 12 }}>
                                        <TouchableOpacity testID="offline-event-preview-back" onPress={() => setStep(previousOfflineEventStep(step, screenMode))} disabled={publishing} activeOpacity={0.85} style={{ paddingVertical: shortWeb ? 12 : 18, paddingHorizontal: 22, borderRadius: 14, borderWidth: 1, borderColor: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.12)', flexDirection: 'row', alignItems: 'center', gap: 8, ...(Platform.OS === 'web' && { cursor: publishing ? 'not-allowed' : 'pointer' } as any) } as any}>
                                            <Ionicons name="arrow-back" size={16} color={tc.text} />
                                            <Text style={{ color: tc.text, fontSize: 14, fontWeight: fontWeights.bold } as any}>Назад</Text>
                                        </TouchableOpacity>
                                        {!isEdit && <TouchableOpacity onPress={handleSaveDraft} disabled={publishing} activeOpacity={0.85} style={{ paddingVertical: shortWeb ? 12 : 18, paddingHorizontal: 22, borderRadius: 14, borderWidth: 1, borderColor: tc.border, alignItems: 'center', justifyContent: 'center' } as any}><Text style={{ color: tc.text, fontFamily: 'Nunito Sans', fontSize: 16, lineHeight: 24, fontWeight: '600' }}>Сохранить черновик</Text></TouchableOpacity>}
                                        <TouchableOpacity testID="offline-event-publish" onPress={() => void handlePublish(false)} disabled={publishing} activeOpacity={0.85} style={orangeBtn(publishing, isDark) as any}>
                                            {publishing ? (
                                                <ActivityIndicator size="small" color={ON_ACCENT_TEXT} />
                                            ) : (
                                                <>
                                                    <Ionicons name={isEdit ? 'save-outline' : (publishToFeed ? 'paper-plane' : 'save-outline')} size={18} color={ON_ACCENT_TEXT} />
                                                    <Text style={{ color: ON_ACCENT_TEXT, fontSize: 15, fontWeight: fontWeights.bold, letterSpacing: 0.5 } as any}>{isEdit ? 'СОХРАНИТЬ ИЗМЕНЕНИЯ' : (publishToFeed ? 'ОПУБЛИКОВАТЬ СОБЫТИЕ' : 'СОХРАНИТЬ ЧЕРНОВИК')}</Text>
                                                </>
                                            )}
                                        </TouchableOpacity>
                                    </View>
                                )}
                            </View>
                        )}

                        {step === 'success' && (
                            <View testID="offline-event-success" style={{ alignItems: 'center', paddingVertical: 12 }}>
                                <View style={{ width: 72, height: 72, borderRadius: 36, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(16,185,129,0.15)', marginBottom: 18 } as any}>
                                    <Ionicons name="checkmark" size={40} color="#10B981" />
                                </View>
                                <Text testID="offline-event-success-title" style={{ fontSize: 24, fontWeight: fontWeights.bold, color: tc.text, textAlign: 'center', marginBottom: 8 } as any}>
                                    {isEdit ? 'Изменения сохранены' : savedAsDraft ? 'Черновик сохранён' : 'Событие опубликовано'}
                                </Text>
                                <Text style={{ fontSize: 14, color: tc.text, opacity: 0.7, textAlign: 'center', marginBottom: 26, lineHeight: 21, maxWidth: 520 } as any}>
                                    {isEdit
                                        ? `«${title}» обновлено. Изменения уже видны в вашем профиле и в календаре.`
                                        : savedAsDraft
                                            ? `«${title}» сохранено как приватный черновик без публикации анонса.`
                                        : publishToFeed
                                            ? `«${title}» опубликовано и доступно во вкладке «События».${publishAnnouncement ? ' Анонс появился в вашей Ленте.' : ''} Публичные события также отображаются в календаре платформы.`
                                            : `«${title}» опубликовано как приватное — доступно по приватной ссылке, из публичных листингов скрыто.`}
                                </Text>

                                {shareUrl && (
                                    <View style={{ width: '100%', maxWidth: 560, alignSelf: 'center', padding: 16, borderRadius: 14, backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', borderWidth: 1, borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)', marginBottom: 22 } as any}>
                                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                                            <Ionicons name="share-social-outline" size={16} color={ACCENT_INK} />
                                            <Text style={{ fontSize: 13, fontWeight: fontWeights.bold, color: tc.text } as any}>Поделиться событием</Text>
                                        </View>
                                        <Text style={{ fontSize: 12, color: tc.text, opacity: 0.65, lineHeight: 18, marginBottom: 12 } as any}>
                                            {publishToFeed
                                                ? 'Отправьте анонс друзьям или скопируйте ссылку.'
                                                : 'Это приватная ссылка — её можно отправить друзьям, чтобы они увидели анонс до публикации.'}
                                        </Text>
                                        <TouchableOpacity testID="offline-event-share" onPress={handleShare} activeOpacity={0.85} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 12, borderRadius: 10, backgroundColor: ACCENT, ...(Platform.OS === 'web' && { cursor: 'pointer' } as any) } as any}>
                                            <Ionicons name="share-social-outline" size={16} color={ON_ACCENT_TEXT} />
                                            <Text style={{ color: ON_ACCENT_TEXT, fontSize: 13, fontWeight: fontWeights.bold } as any}>Поделиться</Text>
                                        </TouchableOpacity>
                                    </View>
                                )}

                                {/* CORE-111 — on edit, the «Перейти к моим
                                    событиям» / «Закрыть» buttons are dropped (the
                                    header ✕ dismisses the modal). They only make
                                    sense for a freshly-created event. */}
                                {!isEdit && (
                                    <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', width: '100%' } as any}>
                                        {navigation && createdEvent && (
                                            <TouchableOpacity
                                                testID="offline-event-go-to-events"
                                                accessibilityRole="button"
                                                accessibilityLabel={publishAnnouncement ? 'Перейти к анонсу в Ленте' : 'Перейти к моим событиям'}
                                                onPress={handleGoToPublishedResult}
                                                activeOpacity={0.85}
                                                style={{ flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: ACCENT, borderRadius: 14, paddingVertical: 16, paddingHorizontal: 24, ...(Platform.OS === 'web' && { cursor: 'pointer', boxShadow: '0 8px 24px rgba(233, 130, 69,0.25)' } as any) } as any}
                                            >
                                                <Ionicons name={publishAnnouncement ? 'newspaper-outline' : 'calendar'} size={16} color={ON_ACCENT_TEXT} accessible={false} />
                                                <Text accessible={false} style={{ color: ON_ACCENT_TEXT, fontSize: 13, fontWeight: fontWeights.bold, letterSpacing: 0.3 } as any}>{publishAnnouncement ? 'ПЕРЕЙТИ К АНОНСУ В ЛЕНТЕ' : 'ПЕРЕЙТИ К МОИМ СОБЫТИЯМ'}</Text>
                                            </TouchableOpacity>
                                        )}
                                        <TouchableOpacity
                                            testID="offline-event-done"
                                            accessibilityRole="button"
                                            accessibilityLabel="Закрыть"
                                            onPress={handleClose}
                                            activeOpacity={0.85}
                                            style={{ paddingVertical: 16, paddingHorizontal: 30, borderRadius: 14, borderWidth: 1, borderColor: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.12)', ...(Platform.OS === 'web' && { cursor: 'pointer' } as any) } as any}
                                        >
                                            <Text accessible={false} style={{ color: tc.text, fontSize: 13, fontWeight: fontWeights.bold, letterSpacing: 0.3 } as any}>ЗАКРЫТЬ</Text>
                                        </TouchableOpacity>
                                    </View>
                                )}
                            </View>
                        )}
                    </ScrollView>
                </View>
            </View>
            <EntityShareModal entity={shareOpen ? shareEntity : null} surface="event_card" onClose={() => setShareOpen(false)} />
        </FlowRoot>
    );
};

export default OfflineEventModal;
